import Link from "next/link";
import { ShareBar, Group, Typography } from "@dreambigger/design-system/src";
import anim from "@dreambigger/shared/src/components/board/styles/animations.module.scss";
import { Stats } from "@dreambigger/shared/src/types";
import { optimizeCloudinaryImg } from "@dreambigger/shared/src/utils";

const { Title, Text } = Typography;

export interface HeroProps {
  communityStats: Stats;
  shareMessageSubject?: string;
  shareMessageBody?: string;
  goalPresent?: boolean;
}

export interface SubStat {
  title: string;
  value: string;
}

const Hero = ({
  communityStats,
  shareMessageSubject,
  shareMessageBody,
  goalPresent,
}: HeroProps) => {
  // Destructure communityStats
  const {
    image,
    title,
    subtitle,
    ctaText,
    ctaURL,
    statistics,
    backgroundColor,
  } = communityStats;

  // Default email share message if none is passed in to component.
  const defaultShareBody = `Hello! I was just browsing this community board and thought you might find the stories on it interesting. 
  
Here is the link if you want to check it out. Maybe you will want to share a story too at some point! - `;
  return (
    // BACKGROUND IMAGE - Is overlaid with a black gradient to showcase the text.
    <div className={`${anim.enter_f} w-100`}>
      <Group
        className="items-center"
        style={{
          background: `linear-gradient(
          rgba(0, 0, 0, 0.25), 
          rgba(0, 0, 0, 0.25)
        ),url(${image && optimizeCloudinaryImg(image)})`,
          backgroundSize: "cover",
          borderRadius: `${
            goalPresent || statistics.length > 0 ? "8px 8px 0 0" : "8px"
          }`,
        }}
      >
        <Group
          spacing={5}
          className="items-center pv-7 md_pv-9 ph-5 tc w-100"
          style={{ maxWidth: 960 }}
        >
          {/* TITLE AND SUBTITLE */}
          <Title className="white">{title}</Title>
          <Text className="white f-5 md_f-6 lh-3">{subtitle}</Text>
          {/* LINK */}
          {ctaURL && ctaText && (
            <Link href={ctaURL} className="white underline f-4">
              {ctaText}
            </Link>
          )}
          {/* SHARE BAR - Only displays when statistics pane is not shown */}
          {/* Note for future develop - shareMessageSubject and shareMessageBody are available if we ever want to customize these messages. */}
          {!statistics ||
            (statistics.length === 0 && (
              <>
                <Text className="white tc ttu f-3 mv-3">Share and Inspire</Text>
                <ShareBar
                  shareMessageSubject={
                    shareMessageSubject
                      ? shareMessageSubject
                      : "Check Out this Community"
                  }
                  shareMessageBody={
                    shareMessageBody ? shareMessageBody : defaultShareBody
                  }
                />
              </>
            ))}
        </Group>
      </Group>
      {/* STATISTICS PANE - Only displays when there are statistics in communityStats meta*/}
      {statistics && (
        <div
          className="flex justify-around"
          style={{
            backgroundColor: backgroundColor || "black",
            borderRadius: `${!goalPresent && "0 0 8px 8px"}`,
          }}
        >
          {statistics.map((stat: SubStat, key: string) => (
            <div key={key} className="pv-2 flex flex-column">
              <p className="mb-0 mt-1 white tc f-2 sm_f-4 fwb ttu lh-1">
                {stat.title}
              </p>
              <p className="mb-0 mt-0 white tc f-5 sm_f-8 fwb ttu lh-1">
                {stat.value}
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Hero;
